<template>
  <div id="app_dashboard_admin">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12">
          <v-card class="mx-auto custom_card_box_dashboard" elevation="0" border="sm opacity-1" rounded="md">
            <v-card flat>
              <template v-slot:title>
                <p class="ml-5 font-weight-bold custom_page_component_main_title">COLLECTIONS ANALYSIS</p>
              </template>
              <template v-slot:subtitle>
                <p class="ml-5 custom_page_main_sub_title_very_small_red" v-if="collectionData">
                  Total: LKR {{ formatCurrency(collectionData.totalChangeAmount || 0) }} |
                  Remaining: LKR {{ formatCurrency(collectionData.totalChangeRemainingAmount || 0) }} |
                  Collected: LKR {{ formatCurrency((collectionData.totalChangeAmount || 0) - (collectionData.totalChangeRemainingAmount || 0)) }}
                </p>
              </template>

              <template v-slot:append>
                <div class="d-flex align-center">
                  <v-menu
                    v-model="fromMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ props }">
                      <v-text-field
                        v-model="fromDateDisplay"
                        label="From Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="props"
                        variant="outlined"
                        density="comfortable"
                        style="width: 180px;"
                        class="custom-date-field"
                        hide-details
                        clearable
                        @click:clear="clearFromDate"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="fromDate"
                      @update:model-value="onFromDateSelect"
                      :max="maxDate"
                      show-adjacent-months
                    ></v-date-picker>
                  </v-menu>

                  <v-menu
                    v-model="toMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ props }">
                      <v-text-field
                        v-model="toDateDisplay"
                        label="To Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="props"
                        variant="outlined"
                        density="comfortable"
                        style="width: 180px; margin-left: 12px;"
                        class="custom-date-field"
                        hide-details
                        clearable
                        @click:clear="clearToDate"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="toDate"
                      @update:model-value="onToDateSelect"
                      :max="maxDate"
                      :min="fromDate || undefined"
                      show-adjacent-months
                    ></v-date-picker>
                  </v-menu>

                  <v-btn
                    v-if="isNonDefaultRange"
                    icon
                    size="small"
                    variant="outlined"
                    @click="clearAllDates"
                    class="ml-2 mr-2"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>

                  <v-btn class="custom_pdf_btn ml-2 mr-4" variant="outlined" @click="generatePdf">
                    <v-icon size="large">mdi-file-pdf</v-icon>&nbsp;&nbsp;PDF
                  </v-btn>
                </div>
              </template>
            </v-card>

            <v-divider></v-divider>

            <v-row class="ma-2" v-if="collectionData">
              <v-col cols="12" sm="6" md="3" v-for="(item, idx) in changeItems" :key="idx">
                <v-card variant="outlined" class="pa-3" :class="item.highlight ? 'bg-blue-lighten-5' : ''">
                  <div class="text-caption text-grey">{{ item.label }}</div>
                  <div class="text-h6 font-weight-bold" :class="item.negative ? 'text-error' : 'text-primary'">
                    LKR {{ formatCurrency(item.value) }}
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <div ref="contentToExport">
              <div class="chart-container pa-4">
                <canvas id="collectionsLineChart"></canvas>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";

export default {
  name: "salesAndCollectionAnalysis",

  props: {},

  data() {
    return {
      maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()),
      fromDate: null,
      toDate: null,
      fromMenu: false,
      toMenu: false,
      fromDateDisplay: '',
      toDateDisplay: '',
      chartInstance: null,
      collectionData: null,
      loading: false,
    };
  },

  computed: {
    changeItems() {
      if (!this.collectionData) return [];
      return [
        {
          label: 'Total Change Amount',
          value: this.collectionData.totalChangeAmount || 0,
          highlight: true
        },
        {
          label: 'Remaining Amount',
          value: this.collectionData.totalChangeRemainingAmount || 0
        },
        {
          label: 'Assigned Amount',
          value: this.collectionData.totalChangeAssignedAmount || 0,
          negative: (this.collectionData.totalChangeAssignedAmount || 0) < 0
        },
        {
          label: 'Actual Remaining',
          value: this.collectionData.totalChangeActualRemainingAmount || 0
        },
      ];
    },

    // Only show the X (clear) button when the selected range differs from the default
    isNonDefaultRange() {
      if (!this.fromDate || !this.toDate) return false;
      const defFrom = this.formatDateDisplay(this.defaultFromDate());
      const defTo = this.formatDateDisplay(this.defaultToDate());
      const curFrom = this.formatDateDisplay(this.fromDate);
      const curTo = this.formatDateDisplay(this.toDate);
      return curFrom !== defFrom || curTo !== defTo;
    }
  },

  watch: {
    fromDate(newVal) {
      this.fromDateDisplay = newVal ? this.formatDateDisplay(newVal) : '';
      this.handleDateChange();
    },
    toDate(newVal) {
      this.toDateDisplay = newVal ? this.formatDateDisplay(newVal) : '';
      this.handleDateChange();
    }
  },

  created() {
    this.initializeDates();
  },

  mounted() {
    this.$nextTick(() => {
      if (!this.fromDate) this.fromDate = this.defaultFromDate();
      if (!this.toDate) this.toDate = this.defaultToDate();

      this.createLineChart();
    });
  },

  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },

  methods: {
    initializeDates() {
      this.fromDate = this.defaultFromDate();
      this.toDate = this.defaultToDate();
      this.fromDateDisplay = this.formatDateDisplay(this.fromDate);
      this.toDateDisplay = this.formatDateDisplay(this.toDate);
    },

    startOfDay(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },

    defaultToDate() {
      return this.startOfDay(new Date());
    },

    defaultFromDate() {
      const today = new Date();
      const from = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
      if (from.getDate() !== today.getDate()) {
        from.setDate(0);
      }
      return from;
    },

    onFromDateSelect(date) {
      this.fromDate = date ? this.startOfDay(new Date(date)) : null;
      this.fromMenu = false;
      if (this.fromDate && this.toDate && this.toDate < this.fromDate) {
        this.toDate = null;
        this.toDateDisplay = '';
      }
    },

    onToDateSelect(date) {
      this.toDate = date ? this.startOfDay(new Date(date)) : null;
      this.toMenu = false;
    },

    clearFromDate() {
      this.fromDate = null;
      this.fromDateDisplay = '';
    },

    clearToDate() {
      this.toDate = null;
      this.toDateDisplay = '';
    },

    // X button → snap back to Part 2's default range (one month ago → today).
    // Setting fromDate/toDate triggers the watchers, which refetch the cards
    // and redraw the chart with the default range.
    clearAllDates() {
      this.fromDate = this.defaultFromDate();
      this.toDate = this.defaultToDate();
      this.fromDateDisplay = this.formatDateDisplay(this.fromDate);
      this.toDateDisplay = this.formatDateDisplay(this.toDate);
    },

    handleDateChange() {
      if (this.fromDate && this.toDate) {
        this.fetchCollectionData();
      }
      this.recreateChart();
    },

    fetchCollectionData() {
      if (!this.fromDate || !this.toDate) return;

      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const baseUrl = process.env.VUE_APP_API_BASE_URL;

      const start = this.formatDateDisplay(this.fromDate);
      const end = this.formatDateDisplay(this.toDate);
      const url = `${baseUrl}/api/dashboard/piyestra?userId=${userId}&fromDate=${start}&toDate=${end}`;

      this.loading = true;

      axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        if (response.data && response.data.success) {
          this.collectionData = response.data.data;
        }
      })
      .catch(error => {
        console.error('Error fetching collection data:', error);
      })
      .finally(() => {
        this.loading = false;
      });
    },

    formatDateDisplay(date) {
      if (!date) return '';
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    generateChartData() {
      let startDate, endDate;

      if (this.fromDate && this.toDate) {
        startDate = new Date(this.fromDate);
        endDate = new Date(this.toDate);
      } else {
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 13);
      }

      const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

      const dates = [];
      const collectedAmounts = [];
      const remainingAmounts = [];
      const totalAmounts = [];

      let baseTotal = 300000;
      let baseCollected = 180000;

      for (let i = 0; i < daysDiff; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        dates.push(date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }));

        const trend = Math.sin(i / daysDiff * Math.PI) * 50000;
        const random = Math.random() * 30000 - 15000;

        const total = Math.max(0, baseTotal + trend + random);
        const collected = Math.min(total, Math.max(0, baseCollected + (trend * 0.6) + (random * 0.5)));
        const remaining = total - collected;

        totalAmounts.push(Math.round(total));
        collectedAmounts.push(Math.round(collected));
        remainingAmounts.push(Math.round(remaining));

        baseTotal = baseTotal + (Math.random() * 10000 - 5000);
        baseCollected = baseCollected + (Math.random() * 8000 - 4000);

        baseTotal = Math.max(200000, Math.min(600000, baseTotal));
        baseCollected = Math.max(100000, Math.min(baseTotal, baseCollected));
      }

      return {
        dates,
        collectedAmounts,
        remainingAmounts,
        totalAmounts
      };
    },

    recreateChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
      this.$nextTick(() => {
        this.createLineChart();
      });
    },

    createLineChart() {
      const canvas = document.getElementById('collectionsLineChart');
      if (!canvas) return;

      const existingChart = Chart.getChart(canvas);
      if (existingChart) {
        existingChart.destroy();
      }

      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      const chartData = this.generateChartData();
      const ctx = canvas.getContext('2d');

      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: chartData.dates,
          datasets: [
            {
              label: 'Total Amount',
              data: chartData.totalAmounts,
              borderColor: '#800080',
              backgroundColor: 'rgba(128, 0, 128, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#800080',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            },
            {
              label: 'Collected Amount',
              data: chartData.collectedAmounts,
              borderColor: '#0057B3',
              backgroundColor: 'rgba(0, 0, 139, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#0057B3',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            },
            {
              label: 'Remaining Amount',
              data: chartData.remainingAmounts,
              borderColor: '#00BFFF',
              backgroundColor: 'rgba(0, 191, 255, 0.1)',
              borderWidth: 3,
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointBackgroundColor: '#00BFFF',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            }
          ]
        },
        options: {
          animation: false,
          responsive: true,
          maintainAspectRatio: true,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: function(context) {
                  let label = context.dataset.label || '';
                  if (label) {
                    label += ': ';
                  }
                  label += 'LKR ' + context.parsed.y.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  });
                  return label;
                }
              }
            },
            legend: {
              position: 'top',
              labels: {
                usePointStyle: true,
                boxWidth: 10,
                font: {
                  size: 12,
                  weight: 'bold'
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return 'LKR ' + value.toLocaleString();
                }
              },
              title: {
                display: true,
                text: 'Amount (LKR)',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              }
            },
            x: {
              title: {
                display: true,
                text: 'Date',
                font: {
                  size: 14,
                  weight: 'bold'
                }
              }
            }
          }
        }
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    async generatePdf() {
      const element = this.$refs.contentToExport;
      if (!element) return;

      try {
        const canvas = await html2canvas(element, { scale: 3, useCORS: true });
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save("Collections_Analysis.pdf");
        this.logDownload('Collections_Analysis.pdf');
      } catch (error) {
        console.error("Error generating PDF:", error);
      }
    },

    async logDownload(fileName) {
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        await axios.post(`/api/log-document-download`, {
          user_id: userId,
          file_type: 'pdf',
          file_name: fileName,
          entity_area: 'Dashboard',
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error logging download:', error);
      }
    },
  },
};
</script>

<style scoped>
.custom-date-field {
  padding-top: 5px !important;
}

.custom-date-field :deep(.v-field--focused .v-label.v-field-label--floating),
.custom-date-field :deep(.v-field--dirty .v-label.v-field-label--floating) {
  top: 5px !important;
}

.chart-container {
  min-height: 400px;
  position: relative;
}

canvas {
  max-height: 500px;
  width: 100% !important;
}
</style>

<style>
@import "../../styles/dashboard/dashboard.scss";
</style>